import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { handleImageError, getResilientFallbackImage } from '../../utils/imageUtils.ts';
import {
  Search,
  BookOpen,
  Calendar,
  Eye,
  ArrowRight,
  Flame,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
} from 'lucide-react';

interface ArticleGridProps {
  badge?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonEnabled?: boolean;
  showSidebar?: boolean;
  limit?: number;
  isPageMode?: boolean;
}

export default function ArticleGrid({
  badge = 'ARTICLES & CLIMATE INSIGHTS',
  title = 'Editorial Insights & Sustainability Intelligence',
  subtitle = 'Latest intelligence on green careers, ESG compliance, clean technology innovations, and climate research.',
  buttonText = 'View All Articles & Insights →',
  buttonEnabled = true,
  limit = 6,
  isPageMode = false,
}: ArticleGridProps) {
  const {
    articles,
    categories,
    navigate,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery,
    settings,
    users,
    openAuthorModal,
  } = useApp();

  const [visibleCount, setVisibleCount] = useState<number>(limit || 6);

  const primaryColor = settings?.theme.primaryColor || '#059669';

  // Filter published articles
  const publishedArticles = articles.filter((a) => a.status === 'published');

  // Helper: compute dynamic category count
  const getCategoryCount = (catId: string, catSlug: string, catName: string) => {
    return publishedArticles.filter((art) => {
      const artCatId = (art.categoryId || '').toLowerCase();
      const artCatName = (art.categoryName || '').toLowerCase();
      return (
        artCatId === catId.toLowerCase() ||
        artCatId === catSlug.toLowerCase() ||
        artCatName === catName.toLowerCase() ||
        artCatName === catSlug.toLowerCase()
      );
    }).length;
  };

  // Filter by category & search
  const filtered = publishedArticles.filter((art) => {
    const artCatId = (art.categoryId || '').toLowerCase();
    const artCatName = (art.categoryName || '').toLowerCase();
    const selCat = (selectedCategory || 'all').toLowerCase();

    const matchesCat =
      selCat === 'all' ||
      artCatId === selCat ||
      artCatName === selCat;

    const query = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !query ||
      (art.title || '').toLowerCase().includes(query) ||
      (art.summary || '').toLowerCase().includes(query) ||
      (art.content || '').toLowerCase().includes(query) ||
      art.tags?.some((t) => (t || '').toLowerCase().includes(query));

    return matchesCat && matchesSearch;
  });

  const visibleArticles = isPageMode ? filtered.slice(0, visibleCount) : filtered.slice(0, limit);

  return (
    <section id="articles-blog-grid-section" className="py-12 md:py-16 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header & Search Bar */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4 pb-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: primaryColor }}
              />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {badge}
              </span>
            </div>
            <h2
              className={`text-2xl sm:text-3xl font-extrabold tracking-tight inline-flex items-center gap-2.5 ${
                !isPageMode ? 'cursor-pointer group hover:text-emerald-700 transition-colors text-slate-900' : (settings?.theme?.categoryTitleColor ? '' : 'text-slate-900')
              }`}
              style={settings?.theme?.categoryTitleColor ? { color: settings.theme.categoryTitleColor } : undefined}
              onClick={() => !isPageMode && navigate('blog')}
            >
              <span>{title}</span>
              {!isPageMode && (
                <span
                  className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-all shadow-2xs group-hover:translate-x-1 shrink-0"
                  title="View All Articles & Insights"
                >
                  <ArrowRight className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                </span>
              )}
            </h2>
            {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
          </div>

          {/* Search Box */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-64">
              <input
                id="article-search-input"
                type="text"
                placeholder="Search articles / keywords..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setVisibleCount(limit || 6);
                }}
                className="w-full pl-10 pr-4 py-2.5 text-xs bg-white border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-emerald-500 shadow-xs text-slate-800"
              />
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
            </div>
          </div>
        </div>

        {searchQuery.trim() && (
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500">Filter tagar aktif:</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 rounded-lg text-xs font-bold border border-emerald-300 shadow-2xs">
              <span>#{searchQuery.replace(/^#/, '')}</span>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  navigate('blog');
                }}
                className="hover:text-red-600 transition-colors cursor-pointer ml-1"
                title="Hapus filter tagar"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          </div>
        )}

        {/* Category Filter Pills */}
        <div className="flex flex-wrap items-center gap-2 mb-8 overflow-x-auto pb-2">
          <button
            id="cat-pill-all"
            onClick={() => {
              setSelectedCategory('all');
              setVisibleCount(limit || 6);
            }}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-2xs ${
              selectedCategory === 'all'
                ? 'text-white shadow-xs'
                : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
            }`}
            style={selectedCategory === 'all' ? { backgroundColor: primaryColor } : {}}
          >
            Semua ({publishedArticles.length})
          </button>

          {categories.map((cat) => {
            const isSelected = selectedCategory === cat.id || selectedCategory === cat.slug;
            const count = getCategoryCount(cat.id, cat.slug, cat.name);
            return (
              <button
                key={cat.id}
                id={`cat-pill-${cat.id}`}
                onClick={() => {
                  setSelectedCategory(cat.id);
                  setVisibleCount(limit || 12);
                }}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-2xs ${
                  isSelected
                    ? 'text-white shadow-xs'
                    : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
                style={isSelected ? { backgroundColor: primaryColor } : {}}
              >
                {cat.name} ({count})
              </button>
            );
          })}
        </div>

        {/* 3-Column Article Grid (No Sidebar) */}
        {visibleArticles.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl border border-dashed border-slate-300 text-center max-w-xl mx-auto shadow-xs">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-slate-700">No matching articles found</h3>
            <p className="text-xs text-slate-400 mt-1">
              Try adjusting your search query or selecting a different category.
            </p>
            <button
              onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
                setVisibleCount(limit || 6);
              }}
              className="mt-4 px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-lg hover:bg-slate-800"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
            {visibleArticles.map((art) => (
              <article
                key={art.id}
                id={`article-card-${art.id}`}
                onClick={() => navigate('article-detail', art.slug || art.id)}
                className="rounded-2xl border overflow-hidden shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col group cursor-pointer"
                style={{
                  backgroundColor: 'var(--article-card-bg, #FFFFFF)',
                  borderColor: 'var(--article-card-border, #E3EBDD)',
                }}
              >
                {/* Cover image */}
                <div className="relative aspect-16/10 overflow-hidden bg-slate-100">
                  <img
                    src={
                      art.coverImage ||
                      getResilientFallbackImage(art.id, art.categoryName)
                    }
                    alt={art.title}
                    onError={(e) => handleImageError(e, art.id, art.categoryName)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCategory(art.categoryId);
                      navigate('blog');
                    }}
                    className="absolute top-3 left-3 bg-slate-900/85 hover:bg-emerald-700 backdrop-blur-xs text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md shadow-xs transition-colors cursor-pointer z-10"
                    title={`View all articles in ${art.categoryName}`}
                  >
                    {art.categoryName}
                  </span>
                  {art.isHeadline && (
                    <span className="absolute top-3 right-3 bg-amber-500 text-slate-900 text-[10px] font-extrabold px-2 py-0.5 rounded-md flex items-center gap-1 shadow-xs">
                      <Flame className="w-3 h-3" />
                      <span>Headline</span>
                    </span>
                  )}
                </div>

                {/* Body */}
                <div className="p-5 sm:p-6 flex-1 flex flex-col justify-between">
                  <div>
                    {/* Meta info */}
                    <div className="flex items-center gap-2.5 text-[11px] mb-2.5" style={{ color: 'var(--article-secondary-text, #5C6B62)' }}>
                      {(() => {
                        const matchedUser = users?.find(
                          (u) =>
                            art.author &&
                            (u.name.trim().toLowerCase() === art.author.trim().toLowerCase() ||
                              u.username.trim().toLowerCase() === art.author.trim().toLowerCase())
                        );
                        const avatarUrl =
                          matchedUser?.avatar ||
                          art.authorAvatar ||
                          'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80';
                        return (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              openAuthorModal(art.author, avatarUrl);
                            }}
                            className="flex items-center gap-1.5 min-w-0 cursor-pointer group/auth hover:underline transition-colors"
                            title={`View profile & bio for ${art.author}`}
                          >
                            <img
                              src={avatarUrl}
                              alt={art.author}
                              className="w-4 h-4 rounded-full object-cover border border-slate-200 shrink-0"
                            />
                            <span className="font-semibold truncate" style={{ color: 'var(--article-body-text, #1F2A24)' }}>
                              {art.author}
                            </span>
                          </div>
                        );
                      })()}
                      <span>&bull;</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 opacity-70" />
                        {new Date(art.createdAt).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    </div>

                    {/* Title */}
                    {(() => {
                      const blogPrefix = (settings?.pageSlugs?.articles || settings?.pageSlugs?.blogSlug || 'blog').replace(/^\/+|\/+$/g, '');
                      const articleUrl = `/${blogPrefix}/${art.slug || art.id}`;
                      return (
                        <a
                          href={articleUrl}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          className="font-bold text-base sm:text-lg leading-snug transition-colors block"
                          style={{ color: 'var(--article-title, #1B4332)' }}
                          title="Open article"
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <span>{art.title}</span>
                          </span>
                        </a>
                      );
                    })()}

                    {/* Summary */}
                    <p className="text-xs sm:text-sm mt-2.5 line-clamp-3 leading-relaxed" style={{ color: 'var(--article-secondary-text, #5C6B62)' }}>
                      {art.summary}
                    </p>

                    {/* Tagar / Hashtags with Target URLs */}
                    {art.tags && art.tags.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-2">
                        {art.tags.slice(0, 3).map((tag, tIdx) => {
                          const cleanTag = tag.trim().replace(/^#/, '');
                          const customUrl =
                            art.tagUrls?.[cleanTag] ||
                            art.tagUrls?.[tag] ||
                            settings?.tagLinks?.[cleanTag] ||
                            settings?.tagLinks?.[tag];
                          const targetUrl = customUrl ? customUrl.trim() : `/tag/${encodeURIComponent(cleanTag)}`;
                          const isExternal = /^https?:\/\//i.test(targetUrl) || /^wa\.me/i.test(targetUrl);
                          const resolvedHref = targetUrl.startsWith('wa.me') ? `https://${targetUrl}` : targetUrl;

                          return (
                            <a
                              key={tIdx}
                              href={resolvedHref}
                              target={isExternal ? '_blank' : '_self'}
                              rel={isExternal ? 'noopener noreferrer' : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isExternal && !customUrl) {
                                  e.preventDefault();
                                  setSearchQuery(cleanTag);
                                  navigate('blog', undefined, { tag: cleanTag });
                                }
                              }}
                              className="px-2 py-0.5 rounded-md text-[10px] font-semibold border transition-all inline-flex items-center gap-1 bg-white hover:bg-emerald-50"
                              style={{
                                borderColor: 'var(--article-card-border, #E3EBDD)',
                                color: 'var(--primary-color, #1B4332)',
                              }}
                              title={customUrl ? `Open link: ${targetUrl}` : `Filter articles tagged #${cleanTag}`}
                            >
                              <span className="font-extrabold" style={{ color: 'var(--accent-color, #2D6A4F)' }}>#</span>
                              <span>{cleanTag}</span>
                              {customUrl && <ExternalLink className="w-2.5 h-2.5 text-emerald-600" />}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Card Footer: Read More */}
                  <div
                    className="mt-5 pt-3 border-t flex items-center justify-end text-xs"
                    style={{ borderColor: 'var(--article-card-border, #E3EBDD)' }}
                  >
                    <span
                      className="font-bold flex items-center gap-1 group-hover:translate-x-1 transition-transform"
                      style={{ color: 'var(--accent-color, #2D6A4F)' }}
                    >
                      <span>Read Full Insight</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Bottom Actions - More button in Page Mode */}
        {isPageMode && filtered.length > visibleCount && (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              id="blog-more-btn"
              onClick={() => setVisibleCount((prev) => prev + (limit || 12))}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-xs sm:text-sm shadow-md hover:shadow-lg hover:opacity-95 active:scale-98 transition-all cursor-pointer"
              style={{ backgroundColor: primaryColor }}
            >
              <ChevronDown className="w-4 h-4 animate-bounce" />
              <span>Muat Lebih Banyak ({filtered.length - visibleCount} artikel tersisa)</span>
            </button>
            <button
              type="button"
              id="blog-show-all-btn"
              onClick={() => setVisibleCount(filtered.length)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white font-bold text-xs sm:text-sm shadow-md hover:bg-slate-800 active:scale-98 transition-all cursor-pointer"
            >
              <BookOpen className="w-4 h-4" />
              <span>Tampilkan Seluruh {filtered.length} Artikel</span>
            </button>
          </div>
        )}

        {/* Home Mode: Link to Full Articles Page */}
        {!isPageMode && (buttonEnabled ?? true) && (
          <div className="mt-10 text-center">
            <button
              type="button"
              id="home-view-all-articles-btn"
              onClick={() => navigate('blog')}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-white font-bold text-sm shadow-md hover:shadow-lg hover:opacity-95 active:scale-98 transition-all cursor-pointer"
              style={{ backgroundColor: primaryColor }}
            >
              <BookOpen className="w-4 h-4" />
              <span>{buttonText || `Lihat Seluruh ${publishedArticles.length} Artikel & Berita →`}</span>
              <ArrowRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
